<template>
  <button :class="['p-button p-component',
    { 'p-button-success': type === 'success' },
    { 'p-button-loading': loading }
  ]" :type="type" @click="handleClick" :disabled="disabled || loading">
    <span v-if="loading" class="spinner">
      <i class="pi pi-spin pi-spinner"></i>
    </span>
    <i v-else-if="icon" :class="iconClasses"></i>
    <span v-if="label" class="p-button-label">{{ label }}</span>
    <slot></slot>
  </button>
</template>

<script>
export default {
  name: 'CustomButton',
  props: {
    label: {
      type: String,
      default: null
    },
    icon: {
      type: String,
      default: null
    },
    type: {
      type: String,
      default: 'button'
    },
    loading: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ['click'],
  computed: {
    iconClasses() {
      if (!this.icon) return '';

      // Si el icono ya incluye 'pi', usarlo directamente
      if (this.icon.includes('pi')) {
        return this.icon;
      }

      // Si no incluye 'pi', agregarlo
      return `pi ${this.icon}`;
    }
  },
  methods: {
    handleClick(event) {
      if (!this.loading && !this.disabled) {
        this.$emit('click', event);
      }
    }
  }
}
</script>

<style scoped>
.p-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 1rem;
  font-size: 1rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
  background-color: #f8f9fa;
  height: 38px;
  margin-top: 20px;
  color: #212529;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s, border-color 0.2s;
}

.p-button:hover:not(:disabled):not(.p-button-loading) {
  background-color: #e9ecef;
}

.p-button-success {
  background-color: #28a745;
  color: #ffffff;
  border-color: #28a745;
}

.p-button-success:hover:not(:disabled):not(.p-button-loading) {
  background-color: #218838;
  border-color: #1e7e34;
}

.p-button:disabled,
.p-button.p-button-loading {
  opacity: 0.6;
  cursor: default;
  pointer-events: none;
}

/* Estilos para iconos PrimeIcons */
.p-button i.pi {
  margin-right: 0.5rem;
  display: inline-block;
  font-size: 1rem;
  font-family: 'PrimeIcons' !important;
  font-style: normal;
  font-weight: normal;
  font-variant: normal;
  text-transform: none;
  line-height: 1;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  visibility: visible !important;
  opacity: 1 !important;
}

/* Cuando el botón solo tiene icono sin label, centrar el icono */
.p-button i.pi:only-child {
  margin-right: 0;
}

/* Asegurar que el icono sea visible y tenga el tamaño correcto */
.p-button i.pi {
  min-width: 1rem;
  min-height: 1rem;
  color: inherit;
}

/* Spinner animation */
.spinner {
  margin-right: 0.5rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

@keyframes spin {
  100% {
    transform: rotate(360deg);
  }
}

.pi-spin {
  animation: spin 1s linear infinite;
}

/* Estilo mejorado para botón con carga */
.p-button-loading {
  position: relative;
  overflow: hidden;
}

.p-button-loading:before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  animation: loading-sweep 1.5s infinite;
}

@keyframes loading-sweep {
  0% {
    transform: translateX(-100%);
  }

  100% {
    transform: translateX(100%);
  }
}

/* Mejorar la legibilidad del texto durante la carga */
.p-button-loading .p-button-label {
  opacity: 0.9;
}
</style>
